import { ShapeLayer, SolidLayer } from '../layers';
import {
  GroupShapeTransform,
  ShapeItem,
  ShapeType,
  ShapeShape,
} from '@libs/waymark-video/video-descriptor-types';

/**
 * Get whether a custom shape is a simple rectangle (called a ShapeShape in our typings for some reason.
 * It's just a shape with custom vertices defined.)
 */
const isRectangularCustomShape = (
  customShape: ShapeShape,
): {
  isRectangle: boolean;
  dimensions: { width: number; height: number } | null;
} => {
  const shapeVertices = customShape.ks;
  if (shapeVertices.a === 1) {
    // If the shape vertices are animated, we can't consider it a rectangle
    return {
      isRectangle: false,
      dimensions: null,
    };
  }

  const verticesConfig = shapeVertices.k;

  const isClosedPath = verticesConfig.c;
  if (!isClosedPath) {
    // If the path is not closed, it can't be a rectangle
    return {
      isRectangle: false,
      dimensions: null,
    };
  }

  const vertices = verticesConfig.v;

  if (vertices.length !== 4) {
    // A rectangle must have exactly 4 vertices
    return {
      isRectangle: false,
      dimensions: null,
    };
  }

  const vertexBezierInPoints = verticesConfig.i;
  const vertexBezierOutPoints = verticesConfig.o;
  if (
    [
      // If any bezier point is not zeroed out, this is not a rectangle
      ...vertexBezierInPoints,
      ...vertexBezierOutPoints,
    ].some((point) => point[0] !== 0 || point[1] !== 0)
  ) {
    return {
      isRectangle: false,
      dimensions: null,
    };
  }

  // A rectangle must have exactly 2 unique x values and 2 unique y values
  const uniqueXValues = new Set<number>();
  const uniqueYValues = new Set<number>();

  for (const vertex of vertices) {
    if (vertex.length !== 2) {
      // Each vertex must be a 2D point
      return {
        isRectangle: false,
        dimensions: null,
      };
    }
    const [x, y] = vertex;
    uniqueXValues.add(x);
    uniqueYValues.add(y);
  }

  if (uniqueXValues.size !== 2 || uniqueYValues.size !== 2) {
    return {
      isRectangle: false,
      dimensions: null,
    };
  }

  const [minX, maxX] = Array.from(uniqueXValues).sort((a, b) => a - b);
  const [minY, maxY] = Array.from(uniqueYValues).sort((a, b) => a - b);

  return {
    isRectangle: true,
    dimensions: {
      width: maxX - minX,
      height: maxY - minY,
    },
  };
};

const getShapeGroupItemsBackgroundGraphicInfo = (
  shapeItems: Array<ShapeItem | GroupShapeTransform>,
): {
  rectangleDimensions: {
    width: number;
    height: number;
  } | null;
  hasFill: boolean;
} | null => {
  // It needs to have a rectangle and a fill to be considered a background graphic
  let rectangleDimensions: { width: number; height: number } | null = null;
  let hasFill = false;

  for (const shape of shapeItems) {
    switch (shape.ty) {
      case ShapeType.Rectangle:
        const [width, height] = shape.s.k;
        if (typeof width !== 'number' || typeof height !== 'number' || width <= 0 || height <= 0) {
          return null;
        }
        rectangleDimensions = {
          width,
          height,
        };
        break;
      case ShapeType.Shape:
        const { isRectangle, dimensions: customShapeDimensions } = isRectangularCustomShape(shape);
        if (!isRectangle) {
          return null;
        }
        if (rectangleDimensions || !customShapeDimensions) {
          // If there is more than one rectangle shape, this is not eligible
          return null;
        }
        rectangleDimensions = customShapeDimensions;
        break;
      case ShapeType.Fill:
      case ShapeType.GradientFill:
        hasFill = true;
        break;
      // Group shape transforms can be skipped
      case 'tr':
        break;
      case ShapeType.Group: {
        const shapeGroupInfo = getShapeGroupItemsBackgroundGraphicInfo(shape.it);
        if (!shapeGroupInfo) {
          return null;
        }
        if (shapeGroupInfo.rectangleDimensions) {
          if (rectangleDimensions) {
            // If there is more than one rectangle shape, this is not eligible
            return null;
          }
          rectangleDimensions = shapeGroupInfo.rectangleDimensions;
        }
        hasFill ||= shapeGroupInfo.hasFill;
        break;
      }
      default:
        if (!shape.hd) {
          // If we encounter any other shapes which aren't hidden,
          // consider this not compatible with being a background graphic
          return null;
        }
    }
  }

  return {
    rectangleDimensions,
    hasFill,
  };
};

/**
 * @returns An object with the width and height of the Shape layer if it is eligible
 *          to be a rectangular background layer, or null if it failed some criteria to be a background layer.
 */
export const getBackgroundShapeLayerDimensions = (
  layer: ShapeLayer,
): {
  width: number;
  height: number;
} | null => {
  const shapeGroupInfo = getShapeGroupItemsBackgroundGraphicInfo(layer.getShapes());
  if (!shapeGroupInfo) {
    return null;
  }

  const { rectangleDimensions, hasFill } = shapeGroupInfo;

  if (!hasFill || !rectangleDimensions) {
    return null;
  }

  const scaleTransformValue = layer.rawLayerData.ks.s;
  if (scaleTransformValue?.a === 1) {
    // If the scale is animated, it's probably not a background graphic
    return null;
  }

  // Scale is expressed as a percentage, so 100 means 100%
  const [scaleX, scaleY] = scaleTransformValue?.k ?? [100, 100];

  const scaledRectangleDimensions = {
    width: (rectangleDimensions.width * scaleX) / 100,
    height: (rectangleDimensions.height * scaleY) / 100,
  };

  return scaledRectangleDimensions;
};

/**
 * @returns An object with the width and height of the solid layer if it is eligible
 *          to be a background layer, or null if it failed some criteria to be a background layer.
 */
export const getBackgroundSolidLayerDimensions = (
  layer: SolidLayer,
): {
  width: number;
  height: number;
} | null => {
  const reactangleDimensions = layer.getUnscaledLayerSize();

  const scaleTransformValue = layer.rawLayerData.ks.s;
  if (scaleTransformValue?.a === 1) {
    // If the scale is animated, it's probably not a background layer; just return null
    return null;
  }

  // Scale is expressed as a percentage, so 100 means 100%
  const [scaleX, scaleY] = scaleTransformValue?.k ?? [100, 100];

  const scaledRectangleDimensions = {
    width: (reactangleDimensions.width * scaleX) / 100,
    height: (reactangleDimensions.height * scaleY) / 100,
  };

  return scaledRectangleDimensions;
};
